import { useEffect, useState, type ReactNode } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DialogHeading } from "@/components/ui/dialog-heading";
import { Button } from "@/components/ui/button";
import { CommonInput } from "@/components/common/CommonInput";
import { InlineLoader } from "@/components/ui/loader";
import { useToast } from "@/hooks/use-toast";
import { authRepository } from "@/repository/authRepository";

interface PasswordResetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  requestPasswordReset?: typeof authRepository.requestPasswordReset;
  sentDescription?: string;
  sentNotice?: string;
  sentActions?: ReactNode;
}

export function PasswordResetDialog({
  isOpen,
  onClose,
  initialEmail = "",
  requestPasswordReset = authRepository.requestPasswordReset,
  sentDescription = "入力されたメールアドレスが登録済みの場合、パスワード再設定リンクを送信しました。",
  sentNotice = "メール内のリンクは30分間、一度だけ使用できます。届かない場合は迷惑メールフォルダをご確認ください。",
  sentActions,
}: PasswordResetDialogProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setEmail((current) => current || initialEmail.trim());
    }
  }, [initialEmail, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await requestPasswordReset({ email: email.trim() });
      setIsSent(true);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      toast({
        title: "エラー",
        description:
          status === 429
            ? "リクエストが多すぎます。時間をおいて再度お試しください。"
            : "メール送信に失敗しました。通信環境を確認して再度お試しください。",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ダイアログを閉じるハンドラー
  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
      setTimeout(() => {
        setEmail("");
        setIsSent(false);
      }, 300);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-md">
        {isSent ? (
          <>
            <DialogHeading
              title="メールをご確認ください"
              description={sentDescription}
            />
            <div className="space-y-4 py-4">
              <p
                className="text-muted-foreground text-sm leading-6"
                role="status"
              >
                {sentNotice}
              </p>
              {sentActions}
              <Button className="w-full" onClick={handleDialogClose}>
                閉じる
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeading
              title="パスワードを再設定"
              description="登録済みのメールアドレスへ、再設定リンクを送信します。"
            />
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <CommonInput
                id="password-reset-email"
                label="メールアドレス"
                placeholder="例: yamada@example.com"
                required
                value={email}
                onChange={handleChange}
                type="email"
                autoComplete="email"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || email.trim().length === 0}
              >
                {isLoading ? (
                  <InlineLoader text="送信中..." />
                ) : (
                  "再設定リンクを送信"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
